import {
  type CustomResourceIconType,
  isCustomResourceIconType,
} from "@app/components/resources/resources_icon_names";
import { SCOPED_PREFIX_POD } from "@app/lib/api/file_system/types";
import { z } from "zod";

export const MAX_POD_FRAME_TABS = 8;
export const MAX_POD_FRAME_TAB_TITLE_LENGTH = 64;
export const DEFAULT_POD_FRAME_TAB_ICON =
  "ActionDashboardIcon" satisfies CustomResourceIconType;

/** System tabs that participate in ordering (Settings is always last and excluded). */
export const POD_NAV_SYSTEM_TABS_BEFORE_SETTINGS = [
  "conversations",
  "tasks",
  "knowledge",
  "files",
  "connected_data",
] as const;

export type PodNavSystemTabBeforeSettings =
  (typeof POD_NAV_SYSTEM_TABS_BEFORE_SETTINGS)[number];

const POD_NAV_SYSTEM_TAB_SET = new Set<string>(
  POD_NAV_SYSTEM_TABS_BEFORE_SETTINGS
);

export function isPodNavSystemTabBeforeSettings(
  value: string
): value is PodNavSystemTabBeforeSettings {
  return POD_NAV_SYSTEM_TAB_SET.has(value);
}

export const PodFrameTabSchema = z.object({
  path: z.string().min(1),
  title: z.string().min(1).max(MAX_POD_FRAME_TAB_TITLE_LENGTH),
  icon: z.custom<CustomResourceIconType>(isCustomResourceIconType, {
    message: "Invalid icon.",
  }),
  // Set when the tab is a pinned dashboard. `path` stays the tab's identity, but the content is
  // fetched by file so the published bundle renders and never the unbuilt source at that path.
  dashboardFileId: z.string().min(1).optional(),
});

export type PodFrameTab = z.infer<typeof PodFrameTabSchema>;

export const PodFrameTabsSchema = z
  .array(PodFrameTabSchema)
  .max(MAX_POD_FRAME_TABS);

/** Mixed list of system tab ids and frame paths (Settings is never included). */
export const PodTabsOrderSchema = z.array(z.string().min(1));

export type PodTabsOrder = z.infer<typeof PodTabsOrderSchema>;

export type PodNavItemBeforeSettings =
  | { kind: "system"; id: PodNavSystemTabBeforeSettings }
  | { kind: "frame"; tab: PodFrameTab };

export function sortPodFrameTabs(tabs: PodFrameTab[]): PodFrameTab[] {
  return [...tabs].sort((a, b) => a.path.localeCompare(b.path));
}

/**
 * Ensure tabsOrder contains every system tab + every frame path exactly once.
 * Unknown entries are dropped; missing ones are appended.
 */
export function normalizeTabsOrder(
  tabsOrder: string[] | null | undefined,
  framePaths: string[]
): string[] {
  const framePathSet = new Set(framePaths);
  const seen = new Set<string>();
  const result: string[] = [];

  for (const entry of tabsOrder ?? []) {
    if (seen.has(entry)) {
      continue;
    }
    if (isPodNavSystemTabBeforeSettings(entry) || framePathSet.has(entry)) {
      result.push(entry);
      seen.add(entry);
    }
  }

  for (const id of POD_NAV_SYSTEM_TABS_BEFORE_SETTINGS) {
    if (!seen.has(id)) {
      result.push(id);
      seen.add(id);
    }
  }

  for (const path of framePaths) {
    if (!seen.has(path)) {
      result.push(path);
      seen.add(path);
    }
  }

  return result;
}

export function visibleTabsOrder(
  tabsOrder: string[],
  { includeConnectedData }: { includeConnectedData: boolean }
): string[] {
  return tabsOrder.filter(
    (id) => id !== "connected_data" || includeConnectedData
  );
}

export function buildPodNavItemsBeforeSettings(
  frameTabs: PodFrameTab[],
  tabsOrder: string[],
  { includeConnectedData }: { includeConnectedData: boolean }
): PodNavItemBeforeSettings[] {
  const byPath = new Map(frameTabs.map((tab) => [tab.path, tab]));
  const normalized = normalizeTabsOrder(
    tabsOrder,
    frameTabs.map((tab) => tab.path)
  );
  const visible = visibleTabsOrder(normalized, { includeConnectedData });

  const items: PodNavItemBeforeSettings[] = [];
  for (const entry of visible) {
    if (isPodNavSystemTabBeforeSettings(entry)) {
      items.push({ kind: "system", id: entry });
      continue;
    }
    const tab = byPath.get(entry);
    if (tab) {
      items.push({ kind: "frame", tab });
    }
  }
  return items;
}

/** Swap a frame with its visible neighbor (system or frame). */
export function moveFrameTabInTabsOrder(
  tabsOrder: string[],
  path: string,
  direction: "left" | "right",
  { includeConnectedData }: { includeConnectedData: boolean }
): string[] | null {
  const visible = visibleTabsOrder(tabsOrder, { includeConnectedData });
  const index = visible.indexOf(path);
  if (index < 0) {
    return null;
  }

  const swapWith = direction === "left" ? index - 1 : index + 1;
  if (swapWith < 0 || swapWith >= visible.length) {
    return null;
  }

  const a = visible[index];
  const b = visible[swapWith];
  const next = [...tabsOrder];
  const ai = next.indexOf(a);
  const bi = next.indexOf(b);
  if (ai < 0 || bi < 0) {
    return null;
  }
  next[ai] = b;
  next[bi] = a;
  return next;
}

/**
 * The path a dashboard would be pinned at, or null when it cannot be pinned. A frame tab is
 * stat'd in the project's own file mount, so a dashboard whose published entry lives anywhere
 * else — a personal or conversation-scoped mount, another project — has no pinnable path.
 */
export function pinnableDashboardTabPath(
  entryScopedPath: string | null,
  podId: string | null
): string | null {
  if (!entryScopedPath || !podId) {
    return null;
  }

  return entryScopedPath.startsWith(`${SCOPED_PREFIX_POD}${podId}/`)
    ? entryScopedPath
    : null;
}

export function podFrameTabBasename(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.(tsx?|jsx?)$/i, "") || base;
}

export function makePodFrameTabValue(path: string): string {
  return `frame:${path}`;
}

export function parsePodFrameTabPath(tab: string): string | null {
  if (!tab.startsWith("frame:")) {
    return null;
  }
  const path = tab.slice("frame:".length);
  return path.length > 0 ? path : null;
}

export function isPodFrameTabValue(tab: string): boolean {
  return parsePodFrameTabPath(tab) !== null;
}
